import * as types from './types'

export const initialStaffingPosition = {
	code: '',
	establishedPost: {
		code: '',
		nameKz: '',
		nameRu: '',
	},
	externalId: '',
	factSalary: '',
	freelance: false,
	grossSalary: '',
	positionCode: '',
	salarySupplement: '',
	staffFreeCount: '',
	staffUnitsCount: '',
}

export const initialStaff = {
	actual: false,
	beginDate: '',
	bin: '',
	childs: [
		{
			binBranch: '',
			childs: [{}],
			dept: {
				code: '',
				nameKz: '',
				nameRu: '',
			},
			deptTypeCode: '',
			externalId: '',
			parentExternalId: '',
			workPlaces: [initialStaffingPosition],
		},
	],
	currVersion: 0,
	externalId: '',
	note: '',
	orderDate: '',
	orderNumber: '',
	parentBin: '',
	esutdInfo: {},
}

const clone = (v) => JSON.parse(JSON.stringify(v || null))

const mergePositionGroups = (apiGroups = [], origGroups = []) => {
	const origMap = new Map((origGroups || []).map((g) => [g.code, g]))
	return apiGroups
		.map((apiPg) => {
			const origPg = origMap.get(apiPg.code)
			if (!origPg) return null

			const wpCodes = (apiPg.workPlaces || []).map((w) => w.code)
			const origWpMap = new Map((origPg.workPlaces || []).map((w) => [w.code, w]))
			const mergedWp = wpCodes.length
				? wpCodes.map((c) => origWpMap.get(c)).filter(Boolean)
				: []

			return {
				...origPg,
				workPlaces: mergedWp,
			}
		})
		.filter(Boolean)
}

const mergeNodeWithApi = (apiNode, origNode) => {
	if (!origNode) return null

	const origChildMap = new Map((origNode.childs || [])
		.map(c => [c?.dept?.code, c])
		.filter(([code]) => code)
	)

	// Мержим дочерние подразделения (API: code, стейт: dept.code)
	const mergedChilds = (apiNode.child || [])
		.map((apiCh) => {
			const origCh = origChildMap.get(apiCh?.code)
			return origCh ? mergeNodeWithApi(apiCh, origCh) : null
		})
		.filter(Boolean)

	// Мержим группы/РМ
	const mergedPg = apiNode.positionGroups
		? mergePositionGroups(apiNode.positionGroups, origNode.positionGroups)
		: origNode.positionGroups || []

	return {
		...origNode,
		childs: mergedChilds.length ? mergedChilds : null,
		positionGroups: mergedPg,
	}
}

const filterStaffingByDeptCodes = (staffing, codes) => {
	if (!staffing || !codes?.size) return staffing
	const walk = (node) => {
		if (!node) return null
		const isMatch = node?.dept?.code && codes.has(node.dept.code)
		const kids = (node.childs || []).map(walk).filter(Boolean)
		if (isMatch || kids.length) {
			return { ...node, childs: kids.length ? kids : null }
		}
		return null
	}
	const childRes = (staffing.childs || []).map(walk).filter(Boolean)
	return { ...staffing, childs: childRes }
}

const collectDeptCodes = (nodes, acc = []) => {
	if (!nodes) return acc

	const list = Array.isArray(nodes) ? nodes : [nodes]

	list.forEach((n) => {
		if (!n) return

		const code = n.code || n?.dept?.code
		if (code) acc.push(code)

		// API: child, штатка: childs (иногда может быть null)
		if (n.child) collectDeptCodes(n.child, acc)
		if (n.childs) collectDeptCodes(n.childs, acc)
	})

	return acc
}

const mergeFilteredWithApi = (original, apiRoots) => {
	if (!original || !apiRoots?.length) return original

	const origChildMap = new Map((original.childs || []).map((c) => [c?.dept?.code, c]).filter(([code]) => code))

	// Пытаемся мерджить с учётом PG/WP (API: code, стейт: dept.code)
	const mergedChilds = apiRoots
		.map((apiRoot) => {
			const origChild = origChildMap.get(apiRoot?.code)
			return origChild ? mergeNodeWithApi(apiRoot, origChild) : null
		})
		.filter(Boolean)

	if (mergedChilds.length) {
		return { ...original, childs: mergedChilds }
	}

	// Показываем хотя бы структуры
	const codes = new Set(collectDeptCodes(apiRoots, []))
	return filterStaffingByDeptCodes(original, codes)
}

const initialState = {
	isLoading: false,
	isSuccess: false,
	error: null,

	isInnSearchActive: false,

	staffing: {
		data: initialStaff,
		isLoading: false,
		isSuccess: false,
	},
	staffingOriginal: null,
	staffingFiltered: null,
	deptSearch: { loading: false, data: null, error: null },
	targetDeptCodes: [],
	openedChildsIds: [],
	staffXML: null,
	wplaceEmployeeList: [],
	employeeListXML: null,
	employeeeDelXML: null,
	employeeEditXML: null,
	structureEditXML: null,
	structureRemoveXML: null,
	groupEditXML: null,
	groupRemoveXML: null,
	workPlaceEditXML: null,
	workPlaceRemoveXML: null,
	innerStatInfo: null,
	wplaceEmployeeStatInfo: null,
}

export const reducer = (state = initialState, action) => {
	const { type, payload } = action
	switch (type) {
		case types.SET_STAFFING: {
			const original = clone(payload)
			return {
				...state,
				isInnSearchActive: false,
				staffingOriginal: original,
				staffingFiltered: original,
				staffing: {
					...state.staffing,
					data: original,
				},
			}
		}

		case types.GET_DEPT_BY_INN:
			return {
				...state,
				isInnSearchActive: true,
				deptSearch: { loading: true, data: null, error: null },
			}

		case types.GET_DEPT_BY_INN_DONE: {
			const apiData = payload?.data || []
			const targetsArr = collectDeptCodes(apiData, [])
			const filtered = mergeFilteredWithApi(state.staffingOriginal, apiData)
			const openedIds = payload?.openedIds || state.openedChildsIds
			return {
				...state,
				isInnSearchActive: true,
				deptSearch: { loading: false, data: apiData, error: null },
				staffingFiltered: filtered,
				staffing: { ...state.staffing, data: filtered },
				openedChildsIds: openedIds,
				targetDeptCodes: targetsArr,
			}
		}
		case types.GET_DEPT_BY_INN_FAIL:
			return {
				...state,
				isInnSearchActive: false,
				deptSearch: { loading: false, data: null, error: payload || true },
				staffingFiltered: state.staffingOriginal,
				staffing: { ...state.staffing, data: state.staffingOriginal },
				targetDeptCodes: [],
			}
		case types.RESET_DEPT_BY_INN:
			return {
				...state,
				isInnSearchActive: false,
				deptSearch: { loading: false, data: null, error: null },
				staffingFiltered: state.staffingOriginal,
				staffing: { ...state.staffing, data: state.staffingOriginal },
				targetDeptCodes: [],
				openedChildsIds: [],
			}
		case types.SET_TARGET_DEPTS:
			return { ...state, targetDeptCodes: payload || [] }
		case types.SET_OPENED_CHILD_IDS:
			return { ...state, openedChildsIds: payload || [] }
		case types.TOGGLE_LOADING:
			return { ...state, isLoading: payload }
		case types.TOGGLE_SUCCESS:
			return { ...state, isSuccess: payload }
		case types.TOGGLE_ERROR:
			return { ...state, error: payload }
		case types.ADD_OPENED_CHILD_ID:
			return {
				...state,
				openedChildsIds: [...state.openedChildsIds, payload],
			}
		case types.REMOVE_OPENED_CHILD_ID:
			return {
				...state,
				openedChildsIds: state.openedChildsIds.filter((id) => id !== payload),
			}
		case types.SET_STAFF_XML:
			return { ...state, staffXML: payload }
		case types.SET_WPLACE_EMPLOYEE:
			return { ...state, wplaceEmployeeList: payload }
		case types.UPDATE_WPLACE_EMPLOYEE:
			const existingEmployeeIndex = state.wplaceEmployeeList.findIndex(
				(employee) => employee.externalId === payload.externalId
			)
			if (existingEmployeeIndex !== -1) {
				const updatedList = [...state.wplaceEmployeeList]
				updatedList[existingEmployeeIndex] = payload
				return { ...state, wplaceEmployeeList: updatedList }
			} else {
				return { ...state, wplaceEmployeeList: [...state.wplaceEmployeeList, payload] }
			}
		case types.DELETE_WPLACE_EMPLOYEE:
			return {
				...state,
				wplaceEmployeeList: state.wplaceEmployeeList.filter((employee) => employee.externalId !== payload),
			}
		case types.RESET_WPLACE_EMPLOYEE:
			return {
				...state,
				wplaceEmployeeList: [],
			}
		case types.SET_EMPLOYEE_XML:
			return { ...state, employeeListXML: payload }
		case types.SET_DEL_EMPLOYEE_XML:
			return { ...state, employeeeDelXML: payload }
		case types.SET_EDIT_EMPLOYEE_XML:
			return { ...state, employeeEditXML: payload }
		case types.SET_EDIT_ACTUAL_STRUCTURE_XML:
			return { ...state, structureEditXML: payload }
		case types.SEND_SIGNED_ACTUAL_STRUCTURE_XML:
			return { ...state, staffing: { ...state.staffing, isLoading: true } }
		case types.SET_REMOVE_ACTUAL_STRUCTURE_XML:
			return { ...state, structureRemoveXML: payload }
		case types.SET_EDIT_ACTUAL_GROUP_XML:
			return { ...state, groupEditXML: payload }
		case types.SEND_SIGNED_ACTUAL_GROUP_XML:
			return { ...state, staffing: { ...state.staffing, isLoading: true } }
		case types.SET_REMOVE_ACTUAL_GROUP_XML:
			return { ...state, groupRemoveXML: payload }
		case types.SET_EDIT_ACTUAL_WORK_PLACE_XML:
			return { ...state, workPlaceEditXML: payload }
		case types.SET_REMOVE_ACTUAL_WORK_PLACE_XML:
			return { ...state, workPlaceRemoveXML: payload }
		case types.GET_STAT_INFO:
			return { ...state, innerStatInfo: payload }
		case types.SET_WPLACE_EMPLOYEE_STAT_INFO:
			return { ...state, wplaceEmployeeStatInfo: payload }
		default:
			return state
	}
}
